import { useMemo, useState } from 'react'
import { useLocation, useParams } from 'wouter'
import { cn } from '@/utils'

import { DraggableTreeNodeContainer } from './DraggableTreeNodeContainer'
import { TreeNodeItem } from './TreeNodeItem'

import { useShallow } from 'zustand/react/shallow'
import { useSelectable } from '@/pages/CADPage/hooks'
import { useCADPageStore } from '@/state'
import { useIsAssemblyNodeLocked } from './hooks/useIsAssemblyNodeLocked'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { findAttachedStep } from '@/pages/CADPage/components/AssemblyTree/lib/findAttachedStep'

import type { RawAssemblyTree, ASMTreeNode } from '@/state'
import type { GLTFObject } from '@/lib/cad/GLTFObject'
import type { DocumentTypeChoices } from '@/lib/api/client'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from '@/components/ui/context-menu'
import { RenameTreeNodeInput } from './RenameTreeNodeInput'

export const TreeNode = ({
  node,
  tree,
  gltf,
  level,
  isReadOnly,
  documentType,
}: {
  node: ASMTreeNode
  tree: RawAssemblyTree
  gltf: GLTFObject
  level: number
  isReadOnly: boolean
  documentType: DocumentTypeChoices
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [showHidePartButton, setShowHidePartButton] = useState(false)

  const isExplosionLinesEnabled = useCADPageStore(
    (state) => state.isExplosionLinesEnabled,
  )
  const selectable = useSelectable({})
  const explosions = useCADPageStore((state) => state.explosions)
  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const isHidden = hiddenParts.includes(node.uuid)

  const selectedParts = useCADPageStore(
    useShallow((state) => state.selectedParts),
  )
  const isSelected = useMemo(
    () => selectedParts.includes(node.uuid),
    [selectedParts, node.uuid],
  )

  const {
    data: { isLocked },
  } = useIsAssemblyNodeLocked({ node })

  const setCadPageState = useCADPageStore(useShallow((state) => state.setState))
  const { steps } = useOperationSteps()

  const attachedStep = useMemo(() => {
    const attachedData = findAttachedStep({ steps, tree, node })
    if (attachedData && attachedData.step && attachedData.step.id) {
      return attachedData.step
    }
  }, [steps, tree, node])

  const [, setLocation] = useLocation()
  const { projectId, documentId } = useParams<{
    projectId?: string
    documentId?: string
  }>()

  const isDraggable =
    documentType === 'work_instructions' &&
    !!node.parent &&
    !isReadOnly &&
    !isLocked

  if (isEditing) {
    return (
      <div
        className="select-none cursor-text flex items-center justify-between space-x-0 py-3 mx-1"
        style={{
          paddingLeft: (level - 1) * 10,
        }}
      >
        <RenameTreeNodeInput
          tree={tree}
          node={node}
          isEditing={isEditing}
          onRename={() => {
            setIsEditing(false)
          }}
        />
      </div>
    )
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger disabled={isReadOnly}>
        <DraggableTreeNodeContainer
          className={cn(
            'hover:bg-primary-10 rounded-lg border border-transparent',
            {
              'hover:bg-primary-20 ': level > 1,
              'hover:bg-primary-20 bg-primary-20': isSelected,
              'select-none cursor-move': isDraggable,
              'bg-gray-100 text-gray-600': isHidden && !isSelected,
            },
          )}
          enabled={isDraggable}
          node={node}
          tree={tree}
          onMouseOver={(e, node) => {
            e.stopPropagation()
            setShowHidePartButton(true)
            selectable
              .handlers(node.uuid, 'assembly', { internalView: true })
              .onSelectHover(e)
          }}
          onMouseOut={(e) => {
            e.stopPropagation()
            setShowHidePartButton(false)
            selectable.onSelectEnd(e)
          }}
        >
          <TreeNodeItem
            tree={tree}
            node={node}
            gltf={gltf}
            isCollapsible={node.children.length > 0}
            isAttachedToOperationStep={isLocked}
            level={level}
            showHidePartButton={showHidePartButton}
          />
        </DraggableTreeNodeContainer>
      </ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem
          onMouseDown={(e) => {
            e.stopPropagation()
            setIsEditing(true)
          }}
        >
          Rename
        </ContextMenuItem>
        {isLocked && attachedStep && projectId && documentId && (
          <ContextMenuItem
            onMouseDown={(e) => {
              e.stopPropagation()

              const stepId = attachedStep.id
              if (stepId) {
                setCadPageState({
                  selectedParts: [],
                  operationStep: {
                    documentId,
                    stepId: attachedStep.id as string,
                    selectFromCad: false,
                    isActive: true,
                  },
                })

                gltf?.setVisibilityForOperationSteps({
                  assemblyTree: tree,
                  steps: steps.filter((s) => s.parent === attachedStep.parent),
                  activeStep: attachedStep,
                  hiddenParts,
                  explosions,
                  isExplosionLinesEnabled,
                })

                setLocation(
                  `/p/${projectId}/document/${documentId}/cad/?page_id=${stepId}`,
                )
              }
            }}
          >
            Open Operation {attachedStep.name}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  )
}
